import type {
  AuthResponse,
  InvitationPreview,
  LoginVerificationSent,
  MeResponse,
  Membership,
  OrganizationSummary,
  User,
} from '@core/types/api';

/**
 * Auth feature types.
 *
 * The wire shapes live in `@core/types/api` (they are shared with the base query
 * and the store) and mirror the DRF serializers field for field; this module
 * re-exports the ones the auth feature speaks in and owns the request payloads.
 * Kura Payments has no signup and no password-reset flow yet, so the surface is
 * deliberately tiny.
 */

export type { InvitationPreview, LoginVerificationSent, Membership, MeResponse, OrganizationSummary, User };

/** Body of `POST /api/auth/login/`. */
export interface LoginRequest {
  email: string;
  password: string;
  /** The language of the sign-in email, when the second step is on. */
  language?: string;
}

/** A session: what a login, a sign-in link or an accepted invitation ends in. */
export type LoginResponse = AuthResponse;

/**
 * Payload of `POST /api/auth/login/`: a session straight away, or, with the emailed
 * second step on (ADR-0022), the notice that the sign-in link was sent.
 */
export type LoginResult = LoginResponse | LoginVerificationSent;

/** Tells the two answers of a login apart. */
export function isVerificationSent(result: LoginResult): result is LoginVerificationSent {
  return (result as LoginVerificationSent).status === 'verification_sent';
}

/** Body of `PATCH /api/auth/me/` — what a person may change about themselves. */
export interface ProfileUpdateRequest {
  first_name: string;
  last_name: string;
}

/** Body of `POST /api/auth/password/` — the current password proves it is them. */
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

/**
 * Body of `POST /api/auth/invitations/<token>/accept/`.
 *
 * One shape for both cases. A brand-new address sends the name it wants and the
 * password it is choosing; an address that already has an account sends the
 * password it already uses, which is what proves the link reached its owner.
 */
export interface AcceptInvitationRequest {
  token: string;
  password: string;
  first_name?: string;
  last_name?: string;
}
